import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { fromEvent, Subscription } from 'rxjs';

export enum SplitterOrientation {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal'
}

@Directive({
  selector: '[wfmSplitter]'
})
export class SplitterDirective implements OnInit, AfterViewInit, OnDestroy {
  private reSizeStart: Subscription;
  private mouseMoveEvent: () => void;
  private mouseUpEvent: () => void;
  private mouseOver: () => void;
  private mouseLeave: () => void;
  public startX = 0;
  public startY = 0;

  @Input() size = 3;
  @Input() orientation: string = SplitterOrientation.VERTICAL;
  @Output() resizingStart = new EventEmitter<boolean>();
  @Output() resizing = new EventEmitter<number>();
  @Output() resizingEnd = new EventEmitter<boolean>();

  constructor(
    @Inject(DOCUMENT) private readonly document: any,
    @Inject(ElementRef)
    private readonly elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.orientation =
      this.orientation === SplitterOrientation.HORIZONTAL
        ? SplitterOrientation.HORIZONTAL
        : SplitterOrientation.VERTICAL;
    let style;
    if (this.orientation === SplitterOrientation.VERTICAL) {
      style = `width: ${this.size + 2}px`;
    } else {
      style = `height: ${this.size + 2}px`;
    }

    this.elementRef.nativeElement.innerHTML = `<div id="wf-splitter" class="flex flex--row flex--nowrap flex--center flex--middle splitter ${this.orientation}"
            style="${style}">
        <!--div class="gutter ${this.orientation}">......</div-->
       </div>`;
  }

  private _oldRect: DOMRect;
  ngAfterViewInit(): void {
    const reSizeStartObservable = fromEvent<MouseEvent>(this.elementRef.nativeElement, 'mousedown');
    this.reSizeStart = reSizeStartObservable.subscribe((e: MouseEvent) => {
      e.preventDefault();
      this.resizingStart.emit(true);
      this._oldRect = this.elementRef.nativeElement.getBoundingClientRect();
      this.mouseMove();
    });

    this.mouseOver = this.renderer.listen(this.elementRef.nativeElement.children.item(0), 'mouseover', () => {
      this.renderer.addClass(this.elementRef.nativeElement.children.item(0), 'mouse-over');
      this.renderer.removeClass(this.elementRef.nativeElement.children.item(0), 'mouse-leave');
    });

    this.mouseLeave = this.renderer.listen(this.elementRef.nativeElement.children.item(0), 'mouseleave', () => {
      this.renderer.addClass(this.elementRef.nativeElement.children.item(0), 'mouse-leave');
      this.renderer.removeClass(this.elementRef.nativeElement.children.item(0), 'mouse-over');
    });
  }

  ngOnDestroy(): void {
    this.reSizeStart && this.reSizeStart.unsubscribe();
    this.mouseOver && this.mouseOver();
    this.mouseLeave && this.mouseLeave();
  }

  private mouseMove() {
    this.mouseMoveEvent = this.renderer.listen('document', 'mousemove', (event: any) => {
      event.preventDefault();
      this.resizingEvent(event);
    });
    this.mouseUpEvent = this.renderer.listen('document', 'mouseup', event => {
      event.preventDefault();
      this.resizeEnd();
    });
  }

  private resizingEvent(event: MouseEvent) {
    let distance = this._oldRect.height + event.clientY - this._oldRect.bottom;
    if (this.orientation === SplitterOrientation.VERTICAL) {
      distance = this._oldRect.width + event.clientX - this._oldRect.right;
      if (this._oldRect.width !== distance) {
        this.resizing.emit(distance);
      }
      return;
    }

    if (this._oldRect.height !== distance) {
      this.resizing.emit(distance);
    }
  }

  private resizeEnd() {
    this.resizingEnd.emit(true);
    this._oldRect = null;
    this.startX = 0;
    this.startY = 0;
    this.mouseMoveEvent && this.mouseMoveEvent();
    this.mouseUpEvent && this.mouseUpEvent();
  }
}
